import React, { ReactElement, useEffect } from 'react';
import useStateRef from 'react-usestateref';
import './pravoEuDetail.scss';
import { Heading } from 'idsk-react-components';
import DOMPurify from 'dompurify';
import PravoEuDetailType from '../../model/PravoEuDetail';
import mapDate from '../../function/mapDate';
import PravoEuDetailRow from '../PravoEuDetailRow';
import PredpisEurlexPrilohaComponent from './component/PredpisEurlexPrilohaComponent';

interface Props {
  pravoEuDetail: PravoEuDetailType;
}

const PravoEuDetail = ({
  pravoEuDetail,
}: Props): ReactElement => {
  const [, setPageOffset, pageOffsetRef] = useStateRef<number>(null);

  useEffect(() => {
    const hasChangedHandler = (): void => {
      if (window.location.hash === '') {
        window.parent.postMessage({
          type: 'scroll-to',
          top: 0,
        }, '*');
      } else if (window.location.hash.startsWith('#positionY')) {
        window.parent.postMessage({
          type: 'scroll-to',
          top: window.location.hash.substring(11),
        }, '*');
      } else {
        document.getElementById(window.location.hash.substring(1))?.scrollIntoView();
      }
    };

    const rememberScrollPosition = (event): void => {
      let target = event.target || event.srcElement;
      target = target.closest('a');
      if (target !== null && target.tagName === 'A') {
        const href = target.getAttribute('href');
        if (href.startsWith('#')) {
          window.location.hash = `positionY-${pageOffsetRef.current}`;
        }
      }
    };

    const handleMessage = (event): void => {
      const message = event.data;
      if (message && message.type === 'pageYOffset') {
        setPageOffset(message.pageYOffset);
      }
    };

    window.addEventListener('hashchange', hasChangedHandler);
    window.addEventListener('click', rememberScrollPosition);
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('hashchange', hasChangedHandler);
      window.removeEventListener('click', rememberScrollPosition);
      window.removeEventListener('message', handleMessage);
    };
  }, [pageOffsetRef, setPageOffset]);

  useEffect(() => {
    const container = document.getElementById('content-container');
    if (container) {
      container.querySelectorAll('a').forEach((link) => {
        const href = link.getAttribute('href');
        if (href?.startsWith('http://') || href?.startsWith('https://')) {
          link.setAttribute('target', '_blank');
        }
      });
    }
  }, [pravoEuDetail.htmlFileContent]);

  return (
    <div className="pravo-eu-detail">
      <PravoEuDetailRow
        header="Celex"
        data={pravoEuDetail.celex}
      />
      <PravoEuDetailRow
        header="Názov predpisu"
        data={pravoEuDetail.nazovPredpisu}
      />
      <PravoEuDetailRow
        header="Zoznam autorov"
        data={pravoEuDetail.zoznamAutorov}
      />
      <PravoEuDetailRow
        header="Typ"
        data={pravoEuDetail.zoznamTypov}
      />
      <PravoEuDetailRow
        header="Zoznam EuroVoc termínov"
        data={pravoEuDetail.zoznamEurovocTerminov}
      />
      <PravoEuDetailRow
        header="Dátum schválenia"
        data={mapDate(pravoEuDetail.datumSchvalenia)}
      />
      <PravoEuDetailRow
        header="Dátum účinnosti od"
        data={mapDate(pravoEuDetail.datumUcinnostiOd)}
      />
      <PravoEuDetailRow
        header="Dátum účinnosti do"
        data={mapDate(pravoEuDetail.datumUcinnostiDo)}
      />
      <PravoEuDetailRow
        header="Dátum uverejnenia"
        data={mapDate(pravoEuDetail.datumUverejnenia)}
      />
      <PravoEuDetailRow
        header="Dátum uzávierky"
        data={mapDate(pravoEuDetail.datumUzavierky)}
      />
      <PravoEuDetailRow
        header="Dátum transpozície"
        data={mapDate(pravoEuDetail.datumTranspozicie)}
      />
      <PravoEuDetailRow
        header="Právny základ"
        data={pravoEuDetail.zoznamPravnychZakladov}
      />
      <PravoEuDetailRow
        header="Zmluva"
        data={pravoEuDetail.zoznamZmluv}
      />
      {pravoEuDetail.prilohaPravoEuList && pravoEuDetail.prilohaPravoEuList.length > 0 && (
      <>
        <Heading className="prilohy-pravo-eu__heading" size="m">Prílohy</Heading>
        {pravoEuDetail.prilohaPravoEuList.map(priloha => (
          <PredpisEurlexPrilohaComponent
            key={priloha.fileName}
            priloha={priloha}
          />
        ))}
      </>
      )}
      <div
        id="content-container"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(pravoEuDetail.htmlFileContent) }}
      />
    </div>
  );
};

export default PravoEuDetail;
